import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, Clock, MapPin, Package, CheckCircle, AlertCircle, Phone, Mail } from 'lucide-react';

const ShippingPolicy = () => {
  const deliveryAreas = [
    { city: 'Mumbai', sameDay: true, nextDay: true, time: 'Before 6 PM' },
    { city: 'Delhi', sameDay: true, nextDay: true, time: 'Before 6 PM' },
    { city: 'Bangalore', sameDay: true, nextDay: true, time: 'Before 6 PM' },
    { city: 'Hyderabad', sameDay: true, nextDay: true, time: 'Before 5 PM' },
    { city: 'Chennai', sameDay: true, nextDay: true, time: 'Before 5 PM' },
    { city: 'Kolkata', sameDay: true, nextDay: true, time: 'Before 5 PM' },
    { city: 'Pune', sameDay: true, nextDay: true, time: 'Before 5 PM' },
    { city: 'Ahmedabad', sameDay: true, nextDay: true, time: 'Before 4 PM' },
  ];

  const shippingRates = [
    { orderValue: 'Below ₹999', charge: '₹99', free: false },
    { orderValue: '₹999 - ₹1999', charge: '₹49', free: false },
    { orderValue: 'Above ₹1999', charge: 'Free', free: true },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-pink-600 to-purple-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-6">
              <Truck className="h-8 w-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Shipping & Delivery Policy</h1>
            <p className="text-xl text-pink-100">
              Information about our delivery services, shipping charges, and delivery timelines
            </p>
          </div>
        </div>
      </div>

      <div className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Delivery Options */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <h2 className="text-3xl font-bold mb-8 flex items-center">
                <Clock className="h-8 w-8 text-pink-600 mr-3" />
                Delivery Options & Timelines
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-gradient-to-br from-pink-50 to-white p-6 rounded-xl border border-pink-100">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mr-4">
                      <Clock className="h-6 w-6 text-pink-600" />
                    </div>
                    <h3 className="text-2xl font-bold">Same Day Delivery</h3>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      <span>Order before 2 PM for same-day delivery</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      <span>Available in major cities</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      <span>Delivery between 4 PM - 9 PM</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      <span>Extra ₹199 charge applies</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mr-4">
                      <Package className="h-6 w-6 text-purple-600" />
                    </div>
                    <h3 className="text-2xl font-bold">Standard Delivery</h3>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      <span>1-2 business days delivery</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      <span>Available across India</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      <span>Delivery between 10 AM - 8 PM</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      <span>Free on orders above ₹999</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Delivery Areas Table */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-6 flex items-center">
                  <MapPin className="h-6 w-6 text-pink-600 mr-3" />
                  Delivery Areas & Cut-off Times
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-pink-50">
                        <th className="p-4 text-left font-bold">City</th>
                        <th className="p-4 text-left font-bold">Same Day Delivery</th>
                        <th className="p-4 text-left font-bold">Next Day Delivery</th>
                        <th className="p-4 text-left font-bold">Cut-off Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveryAreas.map((area, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="p-4 font-medium">{area.city}</td>
                          <td className="p-4">
                            {area.sameDay ? (
                              <span className="inline-flex items-center text-green-600">
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Available
                              </span>
                            ) : 'Not Available'}
                          </td>
                          <td className="p-4">
                            {area.nextDay ? (
                              <span className="inline-flex items-center text-green-600">
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Available
                              </span>
                            ) : 'Not Available'}
                          </td>
                          <td className="p-4">{area.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Shipping Charges */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-6">Shipping Charges</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {shippingRates.map((rate, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-xl">
                      <div className="text-center mb-4">
                        <div className="text-sm text-gray-600 mb-1">Order Value</div>
                        <div className="text-xl font-bold">{rate.orderValue}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Shipping Charge</div>
                        <div className={`text-2xl font-bold ${rate.free ? 'text-green-600' : 'text-gray-900'}`}>
                          {rate.charge}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Important Notes */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <div className="flex items-start">
                  <AlertCircle className="h-6 w-6 text-yellow-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="text-lg font-bold mb-2">Important Delivery Notes</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Delivery timings are estimates and may vary due to traffic, weather, or other unforeseen circumstances</li>
                      <li>• For same-day delivery, orders must be placed before 2 PM. Orders after 2 PM will be delivered the next day</li>
                      <li>• We deliver 7 days a week, including Sundays and most holidays</li>
                      <li>• Please ensure someone is available at the delivery address during the delivery window</li>
                      <li>• For hospital deliveries, please provide the patient's name and room number in the order notes</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Tracking */}
            <div className="bg-gradient-to-r from-pink-600 to-purple-600 rounded-2xl p-8 text-white mb-12">
              <h2 className="text-3xl font-bold mb-6">Track Your Order</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold mb-4">How to Track Your Order</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center mr-3">1</div>
                      <span>Check your email for tracking information</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center mr-3">2</div>
                      <span>Log into your account to view order status</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center mr-3">3</div>
                      <span>Use the tracking link sent via SMS</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center mr-3">4</div>
                      <span>Contact customer support for assistance</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4">Delivery Status Updates</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-green-400 mr-3"></div>
                      <span>Order Confirmed</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-blue-400 mr-3"></div>
                      <span>Processing</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-yellow-400 mr-3"></div>
                      <span>Out for Delivery</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-green-500 mr-3"></div>
                      <span>Delivered</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Section */}
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-6">Need Help with Delivery?</h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Our delivery support team is available 24/7 to assist you with any delivery-related queries
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:18001234567"
                  className="inline-flex items-center justify-center bg-pink-600 text-white px-8 py-4 rounded-full font-bold hover:bg-pink-700 transition-colors"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call Delivery Support
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center border-2 border-pink-600 text-pink-600 px-8 py-4 rounded-full font-bold hover:bg-pink-50 transition-colors"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Email Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;